import type { CollectionSummaryJSONObject, FormSummaryJSONObject } from '@ikegps/core-api';
import { BaseForm, isPLSImportType, plaTypeFromMetadata, type FormModel } from '@ikegps/core-shared';
import { observable, makeObservable } from 'mobx';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { InputState } from '../../components/input/input.state';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { Stepper, type StepperStep } from '../../components/stepper/stepper';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { AppStore } from '../../store';
import { CollectionFormMigrateModalCtrl, type FormMigrateFailure } from './collection.form.migrate.modal.ctrl';
import { MapFieldsView } from './collection.form.migrate.map.fields';
import { MapFieldsState } from './collection.form.migrate.map.fields.state';
import { CSS, Style } from './collection.form.migrate.modal.style';
import { ReviewView } from './collection.form.migrate.review';
import { ReviewState } from './collection.form.migrate.review.state';

addStyles(Style);

type MigrateStep = 'select' | 'mapFields' | 'review';

const STEP_INDEX: { [key in MigrateStep]: number } = {
	select: 0,
	mapFields: 1,
	review: 2,
};

const MIGRATE_STEPS: StepperStep[] = [
	{ label: T.COLLECTION_MIGRATE_FORM_STEP_CHOOSE_FORM },
	{ label: T.COLLECTION_MIGRATE_FORM_STEP_MAP_FIELDS },
	{ label: T.COLLECTION_MIGRATE_FORM_STEP_REVIEW },
];

export class CollectionFormMigrateModal extends ModalFormComponent {
	private ctrl: CollectionFormMigrateModalCtrl;
	private departmentForms: FormSummaryJSONObject[] = [];
	private filteredForms: FormSummaryJSONObject[] = [];
	private errorState: InputState;
	private loadingState: InputState;
	@observable errors: FormMigrateFailure[] = [];
	@observable private selectedFormRevision: string | null = null;
	@observable private step: MigrateStep = 'select';
	private mapFieldsState: MapFieldsState | null = null;
	private reviewState: ReviewState | null = null;

	constructor(ctx: ModalFormComponentCtx, collections: CollectionSummaryJSONObject[]) {
		super(ctx);
		makeObservable(this);

		this.ctrl = new CollectionFormMigrateModalCtrl(collections);
		this.displayCustomButtons = true;

		this.errorState = new InputState(T.ERROR);
		this.loadingState = new InputState(T.LOADING);
		this.addState(this.errorState);
		this.addState(this.loadingState);

		AppStore.FormSummary.refresh();
	}

	get nodes(): MithrilVNode {
		this.departmentForms = AppStore.FormSummary.all;

		if (this.departmentForms.length === 0) {
			this.loadingState.setError(T.LOADING);
			return Spinner.view();
		}

		const collectionFormRevision = this.ctrl.collections[0]?.formRevision;
		const sourceForm = collectionFormRevision ? AppStore.Form.get(collectionFormRevision) : null;
		const sourceIsPLS = Boolean(sourceForm?.metadata.isPLSCADDForm());

		// PLS sources may only migrate to other PLS forms; everything else lists PLA/Base forms.
		this.filteredForms = this.departmentForms.filter((targetForm) => {
			if (!targetForm.metadata || targetForm.revision === collectionFormRevision) {
				return false;
			}

			if (sourceIsPLS) {
				// PLS has known broken forms which we do not want to include as migration target options
				if (isPLSImportType(targetForm.metadata)) {
					return targetForm.revision === targetForm.version;
				}

				return false;
			}

			if (plaTypeFromMetadata(targetForm.metadata) != null) {
				return true;
			}

			// Not a recognized PLA import from metadata alone — there's no metadata-only signal for a
			// plain Base Form, so its full schema has to be checked before ruling it out as custom.
			const fullTargetForm = AppStore.Form.get(targetForm.revision);
			return fullTargetForm != null && BaseForm.isBaseForm(fullTargetForm);
		});

		if (this.filteredForms.length === 0) {
			return m('div', Translate(T.COLLECTION_MIGRATE_FORM_BODY_NO_FORMS));
		}

		this.loadingState.reset();

		if (!this.selectedFormRevision) {
			this.selectedFormRevision = this.filteredForms[0]!.revision;
		}

		const containerClassName = this.step === 'select' ? CSS.container : `${CSS.container} ${CSS.containerWide}`;

		return m('div', { className: containerClassName }, [
			m('div', { className: CSS.stepperWrap }, Stepper.view(MIGRATE_STEPS, STEP_INDEX[this.step])),
			this.viewStepContent(sourceForm),
		]);
	}

	private viewStepContent(sourceForm: FormModel | null): MithrilVNode {
		if (this.step === 'mapFields') {
			return this.viewMapFieldsStep(sourceForm);
		}

		if (this.step === 'review') {
			return this.viewReviewStep(sourceForm);
		}

		return this.viewSelectStep(sourceForm);
	}

	private viewSelectStep(sourceForm: FormModel | null): MithrilVNode {
		return m('div', {}, [
			m('div', { className: CSS.content }, [
				m('div', { className: CSS.intro }, Translate(T.COLLECTION_MIGRATE_FORM_INTRO)),
				m('div', { className: CSS.warning }, [
					m('span', { className: CSS.warningLabel }, Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_TITLE)),
					' ',
					Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT1),
				]),
				m('ul', { className: CSS.list }, [
					m('li', Translate(T.COLLECTION_MIGRATE_FORM_BULLET_DUPLICATE)),
					m('li', Translate(T.COLLECTION_MIGRATE_FORM_BULLET_REVISION_HISTORY)),
				]),
				m('div', { className: CSS.meta }, [
					m(
						'span',
						{ className: CSS.metaBold },
						Translate(T.COLLECTION_MIGRATE_FORM_POLES_SELECTED, { count: this.ctrl.collections.length })
					),
					m('span', { className: CSS.metaMuted }, ' • '),
					m('span', { className: CSS.metaMuted }, `${Translate(T.COLLECTION_MIGRATE_FORM_FROM)} `),
					m('span', { className: CSS.metaBold }, sourceForm?.displayName ?? ''),
				]),
			]),
			m('label', { className: CSS.label, htmlFor: 'form-migrate-target' }, Translate(T.COLLECTION_MIGRATE_FORM_BODY)),
			m('div', { className: CSS.selectWrapper }, [
				m(
					'select',
					{
						id: 'form-migrate-target',
						className: CSS.select,
						onchange: (e) => {
							if ('target' in e && e.target && 'value' in e.target && typeof e.target.value === 'string') {
								this.selectedFormRevision = e.target.value;
							}
						},
					},
					this.filteredForms.map((form) =>
						m('option', { value: form.revision }, `${form.displayname} (V${form.displayversion})`)
					)
				),
				Icon.view(MaterialIcon.KEY_DOWN, { className: CSS.selectChevron }),
			]),
		]);
	}

	private viewMapFieldsStep(sourceForm: FormModel | null): MithrilVNode {
		const targetForm = this.selectedFormRevision ? AppStore.Form.get(this.selectedFormRevision) : null;

		if (sourceForm == null || targetForm == null) {
			return Spinner.view();
		}

		const state = this.getMapFieldsState(sourceForm, targetForm);

		return m('div', { className: CSS.content }, [
			m('div', { className: CSS.mapFieldsHeading }, Translate(T.COLLECTION_MIGRATE_FORM_MAP_FIELDS_HEADING)),
			m('div', { className: CSS.mapFieldsBreadcrumb }, [
				m('span', { className: CSS.metaBold }, sourceForm.displayName),
				m('span', { className: CSS.metaMuted }, ' → '),
				m('span', { className: CSS.metaBold }, `${targetForm.displayName} (V${targetForm.displayVersion})`),
				m('span', { className: CSS.metaMuted }, ' • '),
				m(
					'span',
					{ className: CSS.metaBold },
					Translate(T.COLLECTION_MIGRATE_FORM_POLES_SELECTED, { count: this.ctrl.collections.length })
				),
			]),
			m(
				'div',
				{ className: CSS.mapFieldsCount },
				Translate(T.COLLECTION_MIGRATE_FORM_SOURCE_FIELDS_COUNT, { count: sourceForm.getFieldsRecursive.length })
			),
			MapFieldsView.view({ state }),
			this.viewMapFieldsFooter(state),
		]);
	}

	private viewMapFieldsFooter(state: MapFieldsState): MithrilVNode {
		const fieldsLostCount = state.lostFields.length;

		return m('div', { className: CSS.footerRow }, [
			m('div', { className: CSS.footerNote }, Translate(T.COLLECTION_MIGRATE_FORM_FOOTER_NOTE)),
			fieldsLostCount > 0
				? m('div', { className: CSS.footerFieldsLost }, [
						Icon.view(MaterialIcon.WARNING, { className: CSS.footerFieldsLostIcon }),
						Translate(T.COLLECTION_MIGRATE_FORM_FIELDS_LOST_TOTAL, { count: fieldsLostCount }),
					])
				: null,
		]);
	}

	private getMapFieldsState(sourceForm: FormModel, targetForm: FormModel): MapFieldsState {
		if (
			this.mapFieldsState == null ||
			this.mapFieldsState.sourceForm !== sourceForm ||
			this.mapFieldsState.targetForm !== targetForm
		) {
			this.mapFieldsState = new MapFieldsState(sourceForm, targetForm, this.ctrl.collections);
		}

		return this.mapFieldsState;
	}

	private viewReviewStep(sourceForm: FormModel | null): MithrilVNode {
		const targetForm = this.selectedFormRevision ? AppStore.Form.get(this.selectedFormRevision) : null;

		if (sourceForm == null || targetForm == null) {
			return Spinner.view();
		}

		const state = this.getMapFieldsState(sourceForm, targetForm);
		const reviewState = this.getReviewState(state);

		return ReviewView.view({ sourceForm, targetForm, reviewState });
	}

	/** Recreated (see `viewContinueButton`) each time the user advances into review, since its summary
	 *  is a live derivation of `mapFieldsState` rather than something it needs to preserve across visits. */
	private getReviewState(mapFieldsState: MapFieldsState): ReviewState {
		if (this.reviewState == null || this.reviewState.mapFieldsState !== mapFieldsState) {
			this.reviewState = new ReviewState(mapFieldsState, this.ctrl.collections);
		}

		return this.reviewState;
	}

	override customButtons(): MithrilVNode[] {
		if (this.step === 'mapFields') {
			return [this.viewBackButton('back-to-select', () => (this.step = 'select')), this.viewContinueButton()];
		}

		if (this.step === 'review') {
			return [
				this.viewBackButton('back-to-map-fields', () => (this.step = 'mapFields')),
				m('span', { key: 'submit' }, [this.viewSubmitButton()]),
			];
		}

		return [m('span', { key: 'cancel' }, [this.viewCancelButton()]), this.viewMapFieldsButton()];
	}

	private viewMapFieldsButton() {
		return m(
			'button',
			{
				key: 'map-fields',
				className: 'mdl-button mdl-button--accent',
				disabled: !this.form.canSubmit || this.selectedFormRevision == null,
				type: 'button',
				onclick: () => (this.step = 'mapFields'),
			},
			Translate(T.COLLECTION_MIGRATE_FORM_MAP_FIELDS)
		);
	}

	private viewContinueButton() {
		return m(
			'button',
			{
				key: 'continue',
				className: 'mdl-button mdl-button--accent',
				type: 'button',
				onclick: () => {
					// Force the review step to recompute against whatever mapping was just confirmed.
					this.reviewState = null;
					this.step = 'review';
				},
			},
			Translate(T.COLLECTION_MIGRATE_FORM_CONTINUE)
		);
	}

	private viewBackButton(key: string, onBack: () => void) {
		return m(
			'button',
			{ key, className: 'mdl-button', type: 'button', onclick: onBack },
			Translate(T.COLLECTION_MIGRATE_FORM_BACK)
		);
	}

	override failToChange() {
		this.errors = this.ctrl.failures;
		this.errorState.setError(T.ERROR);
		NotificationService.error({ key: T.COLLECTION_MIGRATE_FORM_FAIL });
	}

	override async submit() {
		if (this.step !== 'review' || this.selectedFormRevision == null || this.mapFieldsState == null) {
			return;
		}

		await this.ctrl.updateFormCollections(this.selectedFormRevision, this.mapFieldsState.toFieldMappingPayload());
	}
}
